<template>
  <v-dialog
    :model-value="modelValue"
    max-width="880px"
    persistent
    scrollable
    @update:model-value="onDialogToggle"
  >
    <v-card>
      <v-card-title class="bg-primary text-white d-flex align-center">
        <v-icon class="mr-2">mdi-shield-lock-outline</v-icon>
        <span class="text-truncate">{{ headerText }}</span>
      </v-card-title>
      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <v-tabs v-model="tab" color="primary" density="comfortable" class="kcc-dialog-tabs px-4">
        <v-tab value="policy" class="text-none" prepend-icon="mdi-stairs">{{ $t('kccPolicy') }}</v-tab>
        <v-tab value="share" class="text-none" prepend-icon="mdi-share-variant">{{ $t('kccShare') }}</v-tab>
      </v-tabs>

      <v-card-text class="pt-4">
        <v-alert v-if="loadError" type="error" variant="tonal" density="compact" class="mb-4">
          {{ loadError }}
        </v-alert>
        <!-- Policy/แชร์ แก้ได้เฉพาะเจ้าของ (Center §5) — panel ซ่อนปุ่มอยู่แล้ว ตรงนี้เช็กซ้ำ -->
        <v-alert v-if="doc && !isOwner" type="warning" variant="tonal" density="compact" class="mb-4">
          {{ $t('kccOwnerOnly') }}
        </v-alert>

        <div v-if="doc" class="d-flex flex-wrap align-center ga-2 mb-4">
          <span class="doc-name">{{ doc.vectordbName }}</span>
          <span v-if="doc.nameTh || doc.nameEn" class="text-body-2 text-medium-emphasis">{{ doc.nameTh || doc.nameEn }}</span>
          <v-chip
            size="small"
            label
            variant="tonal"
            :color="doc.activeStatus ? 'success' : 'secondary'"
            :prepend-icon="doc.activeStatus ? 'mdi-check-circle' : 'mdi-minus-circle-outline'"
          >
            {{ doc.activeStatus ? $t('kccActive') : $t('kccInactive') }}
          </v-chip>
        </div>

        <v-window v-model="tab">
          <!-- ── Policy: ขั้นต่ำ T (ladder ของ Genie) + Channel ที่อนุญาต (ไม่เลือก = ทุก Channel) ── -->
          <v-window-item value="policy">
            <v-alert v-if="policyForm.error" type="error" variant="tonal" density="compact" class="mb-4">
              {{ policyForm.error }}
            </v-alert>
            <v-select
              v-model="policyForm.minLevelCode"
              :items="levelOptions"
              :label="`${$t('kccMinLevel')} *`"
              :disabled="readonly"
              item-title="title"
              item-value="value"
              prepend-inner-icon="mdi-stairs"
              variant="outlined"
              density="comfortable"
              hide-details
              class="mb-4"
            />
            <div class="text-body-2 font-weight-medium mb-2">{{ $t('kccAllowedChannels') }}</div>
            <div class="d-flex flex-wrap align-center ga-2">
              <v-chip
                size="small"
                :color="policyForm.allowedChannels.length ? undefined : 'primary'"
                :variant="policyForm.allowedChannels.length ? 'outlined' : 'flat'"
                :disabled="readonly"
                prepend-icon="mdi-all-inclusive"
                @click="clearChannels"
              >
                {{ $t('kccAllChannels') }}
              </v-chip>
              <v-chip-group
                v-model="policyForm.allowedChannels"
                multiple
                column
                color="primary"
                :disabled="readonly"
              >
                <v-chip
                  v-for="choice in channelChoices"
                  :key="choice.code"
                  :value="choice.code"
                  :base-color="choice.known ? undefined : 'warning'"
                  size="small"
                  variant="outlined"
                  filter
                >
                  {{ choice.code }}<span v-if="choice.name" class="text-medium-emphasis ml-1">· {{ choice.name }}</span>
                </v-chip>
              </v-chip-group>
            </div>
            <div v-if="!readonly" class="d-flex mt-4">
              <v-spacer />
              <v-btn
                color="primary"
                variant="elevated"
                class="text-none"
                :loading="policyForm.saving"
                :disabled="!policyForm.minLevelCode || !policyDirty"
                @click="savePolicy"
              >
                <v-icon start>mdi-content-save</v-icon>
                {{ $t('kccSavePolicy') }}
              </v-btn>
            </div>
          </v-window-item>

          <!-- ── แชร์: รายการผู้รับ + สลับ View/Edit + ยกเลิก (ยืนยัน) + เพิ่มผู้รับ ── -->
          <v-window-item value="share">
            <v-alert v-if="shareForm.error" type="error" variant="tonal" density="compact" class="mb-4">
              {{ shareForm.error }}
            </v-alert>
            <v-table density="compact" class="kcc-share-table">
              <thead>
                <tr>
                  <th>{{ $t('kccShareTo') }}</th>
                  <th>{{ $t('kccPermission') }}</th>
                  <th v-if="!readonly" class="text-center"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!shares.length">
                  <td :colspan="readonly ? 2 : 3" class="text-center text-medium-emphasis py-3">{{ $t('kccNoData') }}</td>
                </tr>
                <tr v-for="share in shares" :key="share.targetCompanyId">
                  <td class="py-2">
                    <div class="font-weight-medium">{{ shareCompanyName(share) }}</div>
                    <div v-if="share.updateDate || share.updateBy" class="text-caption text-medium-emphasis">
                      {{ formatDate(share.updateDate) }}<span v-if="share.updateBy"> · {{ share.updateBy }}</span>
                    </div>
                  </td>
                  <td>
                    <v-btn-toggle
                      :model-value="permissionOf(share)"
                      mandatory
                      divided
                      density="compact"
                      variant="outlined"
                      color="primary"
                      :disabled="readonly || isShareBusy(share.targetCompanyId)"
                      @update:model-value="changePermission(share, $event)"
                    >
                      <v-btn value="View" size="small" class="text-none" prepend-icon="mdi-eye-outline">
                        {{ $t('kccPermissionView') }}
                      </v-btn>
                      <v-btn value="Edit" size="small" class="text-none" prepend-icon="mdi-pencil-outline">
                        {{ $t('kccPermissionEdit') }}
                      </v-btn>
                    </v-btn-toggle>
                  </td>
                  <td v-if="!readonly" class="text-center">
                    <v-btn
                      icon
                      variant="text"
                      density="comfortable"
                      color="error"
                      :disabled="isShareBusy(share.targetCompanyId)"
                      @click="openRemoveShare(share)"
                    >
                      <v-icon size="18">mdi-link-variant-off</v-icon>
                      <v-tooltip activator="parent" location="top">{{ $t('kccRemoveShare') }}</v-tooltip>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-table>

            <!-- เพิ่มผู้รับ: company ที่เปิดอยู่ ยกเว้นเจ้าของ และที่แชร์อยู่แล้ว (สลับสิทธิ์ที่แถวเดิมแทน) -->
            <div v-if="!readonly" class="d-flex flex-wrap align-center ga-3 mt-4">
              <v-select
                v-model="shareForm.targetCompanyId"
                :items="shareTargetOptions"
                :label="$t('kccShareTo')"
                :no-data-text="$t('kccNoData')"
                item-title="title"
                item-value="value"
                prepend-inner-icon="mdi-domain"
                variant="outlined"
                density="comfortable"
                hide-details
                class="kcc-share-target"
              />
              <v-btn-toggle
                v-model="shareForm.permission"
                mandatory
                divided
                density="comfortable"
                variant="outlined"
                color="primary"
              >
                <v-btn value="View" class="text-none" prepend-icon="mdi-eye-outline">{{ $t('kccPermissionView') }}</v-btn>
                <v-btn value="Edit" class="text-none" prepend-icon="mdi-pencil-outline">{{ $t('kccPermissionEdit') }}</v-btn>
              </v-btn-toggle>
              <v-btn
                color="primary"
                variant="elevated"
                class="text-none"
                prepend-icon="mdi-share-variant"
                :loading="shareForm.saving"
                :disabled="shareForm.targetCompanyId == null"
                @click="addShare"
              >
                {{ $t('kccAddShare') }}
              </v-btn>
            </div>
          </v-window-item>
        </v-window>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="outlined" class="text-none" @click="close">
          <v-icon start>mdi-close</v-icon>
          {{ $t('kccClose') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- ยืนยันยกเลิกแชร์ (มีผลทันที — Runtime ของผู้รับอ่านใหม่ทุกคำขอ) -->
    <v-dialog v-model="removeDialog.show" max-width="520px" persistent>
      <v-card>
        <v-card-title class="bg-error text-white d-flex align-center">
          <v-icon class="mr-2">mdi-link-variant-off</v-icon>
          {{ $t('kccRemoveShare') }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" variant="tonal" density="comfortable" class="mb-2">
            {{ $t('kccConfirmRemoveShare', { name: removeDialog.companyName }) }}
          </v-alert>
          <p class="mb-0 doc-name">{{ removeDialog.name }}</p>
          <v-alert v-if="removeDialog.error" type="error" variant="tonal" density="compact" class="mt-4">
            {{ removeDialog.error }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" class="text-none" :disabled="removeDialog.saving" @click="removeDialog.show = false">
            <v-icon start>mdi-close</v-icon>
            {{ $t('kccCancel') }}
          </v-btn>
          <v-btn
            color="error"
            variant="elevated"
            class="text-none"
            :loading="removeDialog.saving"
            :disabled="readonly"
            @click="submitRemoveShare"
          >
            <v-icon start>mdi-link-variant-off</v-icon>
            {{ $t('kccRemoveShare') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
/**
 * Dialog Policy + แชร์ ของเอกสารใน App Knowledge Center (โหมด Center ในหน้า MetaDoc — TASK T9)
 * โครง/สไตล์ลอกจาก `KmcPolicyShareTab.vue` ของ Center (dialog รายละเอียด) แต่เรียก proxy ของ Genie ผ่าน `kccApi`
 *
 * · เปิด ⇒ โหลดพร้อมกัน: `kccApi.documentDetail(name)` (policy + shares สด) · `kccApi.channels()` · `kccApi.companies()` ·
 *   ladder ของ Genie `accessLevelApi.list()` (ล้ม/ว่าง ⇒ `getAccessLevelLadder()` ค่าในตัว) — A12: ladder 2 ฝั่งไม่ sync
 *   ⇒ code ที่ Center ไม่รู้จักจะได้ 400 แสดง inline
 * · Policy → `kccApi.setPolicy({ vectordbName, minLevelCode, allowedChannels })` (`[]` = ทุก Channel)
 * · แชร์ → `kccApi.setShare` (เพิ่ม/สลับ View↔Edit) · `kccApi.removeShare` (ยืนยันก่อน) — server คืนรายการแชร์ทั้งชุด
 * · เฉพาะ scope `owner` (Center §5) — scope อื่น ⇒ อ่านอย่างเดียว + คำเตือน
 * · error inline ทุกจุด: 424 ⇒ `kccKeyRejected` + reason · 502 ⇒ `kccCenterUnreachable` · 412 ⇒ `kccNotConfigured` ·
 *   400/404 ⇒ ข้อความจาก server
 *
 * props `document` (KccDocument จากตาราง) · `initialTab` = `'policy' | 'share'`
 * emit `updated` `{ vectordbName, policy }` หรือ `{ vectordbName, shares }` หลังบันทึกสำเร็จ (panel แทนค่าในแถว + snackbar)
 */
import i18n from '@/plugins/i18n';
import { kccApi, kccErrorKey } from '@/services/kccApi';
import { accessLevelApi } from '@/services/accessLevelApi';
import { getAccessLevelLadder } from '@/utils/accessLevel';
import { fmtThaiDateTime } from '@/utils/thaiTime';

function upperText(value) {
  return value == null ? '' : String(value).trim().toUpperCase();
}

function isThaiLocale() {
  try {
    return String(i18n.global.locale || '').toLowerCase().startsWith('th');
  } catch (e) {
    return false;
  }
}

/** ชื่อแสดงตามภาษาของหน้า (ไทย ⇒ nameTh ก่อน · อื่น ⇒ nameEn ก่อน) */
function localName(item) {
  if (!item) return '';
  return (isThaiLocale() ? item.nameTh || item.nameEn : item.nameEn || item.nameTh) || '';
}

/** 424/502/504/412 ⇒ ข้อความกลางของ Kcc (+ reason ของ Center) · อื่น ๆ ⇒ ข้อความจาก server */
function kccErrorText(t, err) {
  const key = kccErrorKey(err);
  if (key !== 'kccCenterError') {
    const reason = err && err.data && err.data.reason;
    return reason ? `${t(key)} (${reason})` : t(key);
  }
  return (err && err.message) || t('kccCenterError');
}

function emptyPolicyForm() {
  return { minLevelCode: null, allowedChannels: [], saving: false, error: '' };
}

function emptyShareForm() {
  return { targetCompanyId: null, permission: 'View', saving: false, error: '' };
}

function emptyRemoveDialog() {
  return { show: false, name: '', targetCompanyId: null, companyName: '', saving: false, error: '' };
}

export default {
  name: 'KccPolicyShareDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    document: { type: Object, default: null },
    initialTab: { type: String, default: 'policy' },
  },
  emits: ['update:modelValue', 'updated'],
  data() {
    return {
      tab: 'policy',
      doc: null,
      loading: false,
      loadError: '',
      loadSeq: 0,
      levels: [],
      channels: [],
      companies: [],
      policyForm: emptyPolicyForm(),
      shareForm: emptyShareForm(),
      // targetCompanyId ที่กำลังสลับสิทธิ์อยู่ (กันกดซ้ำระหว่างรอ server)
      busyShareTargets: [],
      removeDialog: emptyRemoveDialog(),
    };
  },
  computed: {
    headerText() {
      const name = this.doc && this.doc.vectordbName;
      const label = `${this.$t('kccPolicy')} · ${this.$t('kccShare')}`;
      return name ? `${label} — ${name}` : label;
    },
    isOwner() {
      return !!this.doc && String(this.doc.scope || '').toLowerCase() === 'owner';
    },
    /** ไม่ใช่เจ้าของ ⇒ อ่านอย่างเดียว · ระหว่างโหลด detail ก็ล็อก (ฟอร์มจะถูกตั้งใหม่จากค่าสดเมื่อโหลดเสร็จ) */
    readonly() {
      return !this.doc || !this.isOwner || this.loading;
    },
    /** dropdown ขั้นต่ำ `[{ title: "T2 — ชื่อ", value: "T2" }]` ตามลำดับ ladder */
    levelOptions() {
      const options = (Array.isArray(this.levels) ? this.levels : [])
        .filter((level) => level && level.code)
        .map((level) => {
          const name = localName(level);
          return { title: name ? `${level.code} — ${name}` : level.code, value: upperText(level.code) };
        });
      // ขั้นต่ำเดิมที่ ladder ของ Genie ไม่รู้จัก (ladder 2 ฝั่งไม่ sync) — คงไว้ให้เห็น
      const current = upperText(this.policyForm.minLevelCode);
      if (current && !options.some((option) => option.value === current)) {
        options.push({ title: current, value: current });
      }
      return options;
    },
    /** ขั้นล่างสุดของ ladder — ค่าเริ่มต้นเมื่อเอกสารยังไม่มี Policy (Center ถือ T0 + ทุก Channel) */
    bottomLevelCode() {
      const first = (Array.isArray(this.levels) ? this.levels : []).find((level) => level && level.code);
      return first ? upperText(first.code) : null;
    },
    /**
     * chip Channel = Channel ที่เปิดใช้ (ตาม sortOrder) + code ที่ Policy เดิมเลือกไว้แต่ไม่อยู่ในรายการ (ถูกปิด/ลบ —
     * แสดงสีเตือน · Center จะไม่รับตอนบันทึก ให้ผู้ใช้เอาออก)
     */
    channelChoices() {
      const choices = (Array.isArray(this.channels) ? this.channels : [])
        .filter((channel) => channel && channel.code && channel.isActive !== false)
        .slice()
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        .map((channel) => ({ code: upperText(channel.code), name: localName(channel), known: true }));
      const known = new Set(choices.map((choice) => choice.code));
      this.policyForm.allowedChannels.forEach((code) => {
        if (!known.has(code)) choices.push({ code, name: '', known: false });
      });
      return choices;
    },
    shares() {
      const list = this.doc && this.doc.shares;
      return Array.isArray(list) ? list : [];
    },
    /** เปลี่ยนจากค่าที่ Center มีหรือยัง (ยังไม่เคยตั้ง Policy ⇒ บันทึกค่าเริ่มต้นลง DB ได้) */
    policyDirty() {
      if (!this.doc) return false;
      const policy = this.policyOf(this.doc);
      if (!policy.fromDatabase) return true;
      if (upperText(policy.minLevelCode) !== upperText(this.policyForm.minLevelCode)) return true;
      const saved = this.channelsOf(this.doc).slice().sort().join(',');
      const current = this.policyForm.allowedChannels.slice().sort().join(',');
      return saved !== current;
    },
    companyById() {
      const map = {};
      (Array.isArray(this.companies) ? this.companies : []).forEach((company) => {
        if (company && company.companyId != null) map[company.companyId] = company;
      });
      return map;
    },
    /** company ที่แชร์เพิ่มได้ = ทุก company ที่ Center คืน (เปิดอยู่) ยกเว้นเจ้าของ และที่แชร์อยู่แล้ว (`0` เป็น id จริง) */
    shareTargetOptions() {
      if (!this.doc) return [];
      const taken = new Set(this.shares.map((share) => share.targetCompanyId));
      const ownerId = this.doc.ownerCompanyId;
      return (Array.isArray(this.companies) ? this.companies : [])
        .filter((company) => company && company.companyId != null)
        .filter((company) => company.companyId !== ownerId && !taken.has(company.companyId))
        .map((company) => ({ title: company.companyName || `#${company.companyId}`, value: company.companyId }));
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(open) {
        if (open) this.open();
      },
    },
  },
  methods: {
    formatDate(value) {
      return fmtThaiDateTime(value);
    },
    errorTextOf(err) {
      return kccErrorText((key) => this.$t(key), err);
    },
    policyOf(doc) {
      return (doc && doc.policy) || { minLevelCode: '', allowedChannels: [], fromDatabase: false };
    },
    channelsOf(doc) {
      const list = this.policyOf(doc).allowedChannels;
      return (Array.isArray(list) ? list : []).map(upperText).filter(Boolean);
    },
    permissionOf(share) {
      return String((share && share.permission) || '').toLowerCase() === 'edit' ? 'Edit' : 'View';
    },
    shareCompanyName(share) {
      const company = this.companyById[share.targetCompanyId];
      return share.targetCompanyName || (company && company.companyName) || `#${share.targetCompanyId}`;
    },
    isShareBusy(targetCompanyId) {
      return this.busyShareTargets.includes(targetCompanyId);
    },
    resetForms(doc) {
      const policy = this.policyOf(doc);
      this.policyForm = {
        ...emptyPolicyForm(),
        minLevelCode: upperText(policy.minLevelCode) || this.bottomLevelCode,
        allowedChannels: this.channelsOf(doc),
      };
      this.shareForm = emptyShareForm();
      this.busyShareTargets = [];
    },
    onDialogToggle(value) {
      if (!value) this.close();
    },
    close() {
      this.loadSeq += 1;
      this.loading = false;
      this.removeDialog = emptyRemoveDialog();
      this.$emit('update:modelValue', false);
    },

    // ── โหลด ─────────────────────────────────────────────────────────────
    async open() {
      const doc = this.document;
      this.tab = this.initialTab === 'share' ? 'share' : 'policy';
      this.doc = doc || null;
      this.loadError = '';
      this.removeDialog = emptyRemoveDialog();
      this.levels = getAccessLevelLadder().slice();
      this.resetForms(doc);
      if (!doc || !doc.vectordbName) return;

      const seq = this.loadSeq + 1;
      this.loadSeq = seq;
      this.loading = true;
      const [detail, channels, companies, ladder] = await Promise.allSettled([
        kccApi.documentDetail(doc.vectordbName),
        kccApi.channels(),
        kccApi.companies(),
        accessLevelApi.list(),
      ]);
      if (seq !== this.loadSeq) return;

      const errors = [];
      if (ladder.status === 'fulfilled' && ladder.value && Array.isArray(ladder.value.levels) && ladder.value.levels.length) {
        this.levels = ladder.value.levels;
      }
      if (channels.status === 'fulfilled') {
        this.channels = Array.isArray(channels.value) ? channels.value : [];
      } else {
        errors.push(this.errorTextOf(channels.reason));
      }
      if (companies.status === 'fulfilled') {
        this.companies = Array.isArray(companies.value) ? companies.value : [];
      } else {
        errors.push(this.errorTextOf(companies.reason));
      }
      if (detail.status === 'fulfilled' && detail.value) {
        // ผู้เรียกที่ไม่ใช่เจ้าของได้ shares = null — คง scope เดิมจากตารางถ้า detail ไม่ส่งมา
        this.doc = { ...doc, ...detail.value, scope: detail.value.scope || doc.scope };
      } else if (detail.status === 'rejected') {
        // 404 = เอกสารถูกลบ/เปลี่ยนไปแล้ว — คงข้อมูลจากตารางไว้ให้ดู แต่แจ้ง error
        errors.push(this.errorTextOf(detail.reason));
      }
      this.resetForms(this.doc);
      this.loadError = [...new Set(errors.filter(Boolean))].join(' · ');
      this.loading = false;
    },
    clearChannels() {
      if (this.readonly) return;
      this.policyForm.allowedChannels = [];
    },
    patchDoc(patch) {
      if (!this.doc) return;
      this.doc = { ...this.doc, ...patch };
      this.$emit('updated', { vectordbName: this.doc.vectordbName, ...patch });
    },

    // ── Policy ───────────────────────────────────────────────────────────
    async savePolicy() {
      const doc = this.doc;
      const form = this.policyForm;
      if (this.readonly || !doc || form.saving || !form.minLevelCode) return;
      form.saving = true;
      form.error = '';
      try {
        const allowedChannels = form.allowedChannels.map(upperText).filter(Boolean);
        const policy = await kccApi.setPolicy({
          vectordbName: doc.vectordbName,
          minLevelCode: upperText(form.minLevelCode),
          allowedChannels,
        });
        const saved = policy || {
          vectordbName: doc.vectordbName,
          minLevelCode: upperText(form.minLevelCode),
          allowedChannels,
          fromDatabase: true,
        };
        this.patchDoc({ policy: saved });
        this.policyForm = {
          ...emptyPolicyForm(),
          minLevelCode: upperText(saved.minLevelCode),
          allowedChannels: this.channelsOf({ policy: saved }),
        };
      } catch (err) {
        // 400 = ขั้นต่ำไม่อยู่ใน ladder ของ Center / Channel ไม่รู้จักหรือปิดอยู่ · 404 = เอกสารไม่มีแล้ว
        form.error = this.errorTextOf(err);
      } finally {
        form.saving = false;
      }
    },

    // ── แชร์ ─────────────────────────────────────────────────────────────
    applyShares(shares) {
      if (Array.isArray(shares)) this.patchDoc({ shares });
    },
    async changePermission(share, permission) {
      const doc = this.doc;
      if (this.readonly || !doc || !share || !permission || permission === this.permissionOf(share)) return;
      if (this.isShareBusy(share.targetCompanyId)) return;
      this.busyShareTargets = [...this.busyShareTargets, share.targetCompanyId];
      this.shareForm.error = '';
      try {
        const shares = await kccApi.setShare({
          vectordbName: doc.vectordbName,
          targetCompanyId: share.targetCompanyId,
          permission,
        });
        this.applyShares(shares);
      } catch (err) {
        this.shareForm.error = this.errorTextOf(err);
      } finally {
        this.busyShareTargets = this.busyShareTargets.filter((id) => id !== share.targetCompanyId);
      }
    },
    async addShare() {
      const doc = this.doc;
      const form = this.shareForm;
      if (this.readonly || !doc || form.saving || form.targetCompanyId == null) return;
      form.saving = true;
      form.error = '';
      try {
        const shares = await kccApi.setShare({
          vectordbName: doc.vectordbName,
          targetCompanyId: form.targetCompanyId,
          permission: form.permission === 'Edit' ? 'Edit' : 'View',
        });
        this.applyShares(shares);
        this.shareForm = emptyShareForm();
      } catch (err) {
        // 400 = แชร์ให้เจ้าของเอง / company ไม่มีจริง
        form.error = this.errorTextOf(err);
      } finally {
        form.saving = false;
      }
    },
    openRemoveShare(share) {
      const doc = this.doc;
      if (this.readonly || !doc || !share) return;
      this.removeDialog = {
        ...emptyRemoveDialog(),
        show: true,
        name: doc.vectordbName,
        targetCompanyId: share.targetCompanyId,
        companyName: this.shareCompanyName(share),
      };
    },
    async submitRemoveShare() {
      const dialog = this.removeDialog;
      if (this.readonly || dialog.saving || !dialog.name || dialog.targetCompanyId == null) return;
      dialog.saving = true;
      dialog.error = '';
      try {
        const shares = await kccApi.removeShare(dialog.name, dialog.targetCompanyId);
        this.applyShares(Array.isArray(shares) ? shares : this.shares.filter((s) => s.targetCompanyId !== dialog.targetCompanyId));
        dialog.show = false;
      } catch (err) {
        dialog.error = this.errorTextOf(err);
      } finally {
        dialog.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.kcc-dialog-tabs {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.doc-name {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-weight: 600;
  word-break: break-all;
}

.kcc-share-table {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
}

.kcc-share-table :deep(thead th) {
  font-weight: 600 !important;
}

.kcc-share-target {
  max-width: 360px;
  min-width: 240px;
}
</style>
